import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Folder as FolderIcon,
    MoreVert as MoreVertIcon,
    Search as SearchIcon,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Menu,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    deleteChecklistLogically,
    deleteMultipleChecklists,
    fetchChecklists,
    updateChecklist,
} from "../api/checklist";
import {
    createFolder,
    deleteFolder,
    getFolders,
    updateFolder,
} from "../api/folder";
import {
    CancelIconButton,
    DeleteIconButton,
    EditIconButton,
    SaveIconButton,
} from "../components/Button";
import Loading from "../components/Loading";
import PageHeader from "../components/layout/PageHeader";

// Alert コンポーネントを定義 (Snackbar内で使用)
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// searchFieldsをコンポーネントの外部で定義
// const searchFields = {
//   title: "title",
//   assignee: "assignee",
// };

const TRASH_FOLDER_NAME = "ゴミ箱";
const PAGE_SIZE = 20;

function SelectCheckList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // nullは未分類を示す
  const [checklists, setChecklists] = useState([]);
  const [editData, setEditData] = useState({});
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [activeSearch, setActiveSearch] = useState({ text: "", type: "all" });
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [checklistToDelete, setChecklistToDelete] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    size: PAGE_SIZE,
    total: 0,
    pages: 1,
  });

  // Bulk delete states
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [selectedChecklists, setSelectedChecklists] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [addFolderDialogOpen, setAddFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [folderMenuAnchorEl, setFolderMenuAnchorEl] = useState(null);
  const [menuFolder, setMenuFolder] = useState(null); // 3点リーダーで操作中のフォルダ
  const [editingFolder, setEditingFolder] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(null);

  const fetchAndSetFolders = useCallback(async () => {
    try {
      const folderData = await getFolders();
      setFolders(folderData);
    } catch (error) {
      console.error("Failed to fetch folders:", error);
      setSnackbarMessage("フォルダの読み込みに失敗しました。");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, []);

  const fetchAndSetChecklists = useCallback(
    async (pageToFetch = 1) => {
      setLoading(true);
      try {
        const data = await fetchChecklists(
          selectedFolderId,
          pageToFetch,
          PAGE_SIZE,
          activeSearch.text,
          activeSearch.type
        );

        const formattedChecklists = data.items.map((checklist) => ({
          id: checklist.id,
          public_id: checklist.public_id,
          title: checklist.name,
          assignee: checklist.assignee,
          folder_id: checklist.folder_id,
          folder_public_id: checklist.folder_public_id,
          isEditing: false,
        }));
        setChecklists(formattedChecklists);
        setPagination({
          total: data.total,
          page: data.page,
          size: data.size,
          pages: data.pages,
        });
      } catch (error) {
        console.error("Failed to fetch checklists:", error);
        setSnackbarMessage("チェックリストの読み込みに失敗しました。");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    },
    [selectedFolderId, activeSearch]
  );

  useEffect(() => {
    fetchAndSetFolders();
  }, [fetchAndSetFolders]);

  useEffect(() => {
    // フォルダ選択、検索条件の変更時に1ページ目から再取得
    fetchAndSetChecklists(1);
  }, [selectedFolderId, activeSearch, fetchAndSetChecklists]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setActiveSearch((prev) => {
        if (prev.text === searchText && prev.type === searchType) {
          return prev;
        }
        return { text: searchText, type: searchType };
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText, searchType]);

  // ページ変更ハンドラ
  const handlePageChange = (event, value) => {
    fetchAndSetChecklists(value);
  };

  // 初回データ読み込み (削除)
  // フォルダ選択時のチェックリスト読み込み (fetchAndSetChecklists に統合)
  const handleFolderSelect = useCallback((folderId) => {
    setSelectedFolderId(folderId);
    // ページはuseEffectによってリセットされる
  }, []);

  // 検索処理 (バックエンドに移行したため不要)
  // const filteredChecklists = filterItems(
  //   checklists,
  //   searchText,
  //   searchType,
  //   searchFields
  // );

  // 編集モード開始
  const handleEditStart = (checklist) => {
    setChecklists(
      checklists.map((item) => ({
        ...item,
        isEditing: item.id === checklist.id,
      }))
    );
    setEditData({
      title: checklist.title,
      assignee: checklist.assignee,
      folder_id: checklist.folder_id || "unclassified",
    });
  };

  // 編集をキャンセル
  const handleEditCancel = () => {
    setChecklists(
      checklists.map((item) => ({
        ...item,
        isEditing: false,
      }))
    );
    setEditData({});
  };

  // 編集を保存
  const handleEditSave = async (checklist) => {
    try {
      const response = await updateChecklist(checklist.public_id, {
        name: editData.title,
        assignee: editData.assignee,
        folder_id:
          editData.folder_id === "unclassified" ? null : editData.folder_id,
      });

      if (!response.ok) {
        let errorDetail = `サーバーエラー: ${response.status}`;
        try {
          const errorJson = await response.json();
          errorDetail = errorJson.detail || errorDetail;
        } catch (e) {
          errorDetail = (await response.text()) || errorDetail;
        }
        throw new Error(errorDetail);
      }

      const updatedChecklist = await response.json();

      const destinationFolderId =
        editData.folder_id === "unclassified" ? null : editData.folder_id;

      setChecklists((prev) =>
        prev
          .map((item) =>
            item.id === checklist.id
              ? {
                  ...item,
                  title: updatedChecklist.name,
                  assignee: updatedChecklist.assignee,
                  folder_id: destinationFolderId,
                  folder_public_id: updatedChecklist.folder_public_id,
                  isEditing: false,
                }
              : item
          )
          // フォルダ変更で現在のフィルタから外れた場合は除外
          .filter((item) =>
            selectedFolderId === null || selectedFolderId === "unclassified"
              ? item.folder_public_id === null || item.folder_public_id === undefined
              : item.folder_public_id === selectedFolderId
          )
      );
      setEditData({});
      setSnackbarMessage("チェックリストを更新しました。");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to update checklist:", error);
      setSnackbarMessage(`更新に失敗しました: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // チェックリストクリック時の処理
  const handleChecklistClick = (checklistPublicId) => {
    navigate(`/checklists/${checklistPublicId}`);
  };

  const handleCreateChecklistFromTemplate = () => {
    navigate("/templates/select-template?creationMode=checklist");
  };

  // 削除ボタンクリック時の処理
  const handleDeleteClick = (e, checklist) => {
    e.stopPropagation();
    setChecklistToDelete(checklist);
    setSelectedChecklists([]); // ensure bulk list is empty for individual delete
    setConfirmDeleteDialogOpen(true);
  };

  // （旧）個別削除用の handleDeleteConfirm / handleCloseConfirmDeleteDialog は
  // bulk 対応で統合したため削除しました。

  // Snackbar を閉じる処理
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) {
      setSnackbarMessage("フォルダ名を入力してください。");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    try {
      await createFolder(newFolderName.trim());
      setNewFolderName("");
      setAddFolderDialogOpen(false);
      setSnackbarMessage("フォルダを作成しました。");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchAndSetFolders(); // フォルダリストを再読み込み
    } catch (error) {
      console.error("Failed to create folder:", error);
      setSnackbarMessage(`フォルダの作成に失敗しました: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleFolderMenuOpen = (event, folder) => {
    event.stopPropagation();
    setFolderMenuAnchorEl(event.currentTarget);
    setMenuFolder(folder);
  };

  const handleFolderMenuClose = () => {
    setFolderMenuAnchorEl(null);
    setMenuFolder(null);
  };

  const handleUpdateFolder = async () => {
    if (!editingFolder || !editingFolder.name.trim()) {
      setSnackbarMessage("フォルダ名を入力してください。");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    try {
      await updateFolder(editingFolder.public_id, editingFolder.name.trim());
      handleFolderMenuClose();
      setEditingFolder(null);
      setSnackbarMessage("フォルダ名を変更しました。");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchAndSetFolders();
    } catch (error) {
      console.error("Failed to update folder:", error);
      setSnackbarMessage(`フォルダ名の変更に失敗しました: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDeleteFolder = async () => {
    if (!deletingFolder) return;
    try {
      await deleteFolder(deletingFolder.public_id);
      setDeletingFolder(null);
      setSnackbarMessage(`「${deletingFolder.name}」フォルダを削除しました。`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchAndSetFolders();
      // 削除されたフォルダが表示されていた場合、未分類に移動
      if (selectedFolderId === deletingFolder.public_id) {
        handleFolderSelect(null);
      }
    } catch (error) {
      console.error("Failed to delete folder:", error);
      setSnackbarMessage(`フォルダの削除に失敗しました: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // ----------------------------
  // Delete mode helpers
  // ----------------------------

  const toggleDeleteMode = () => {
    if (isDeleteMode) {
      // cancel
      setIsDeleteMode(false);
      setSelectedChecklists([]);
    } else {
      setIsDeleteMode(true);
    }
  };

  const handleSelectChecklistForDelete = (id) => {
    setSelectedChecklists((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  const handleBulkDeleteClick = () => {
    if (selectedChecklists.length === 0) return;
    setConfirmDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteDialogOpen(false);
    setChecklistToDelete(null);
    setSelectedChecklists([]);
  };

  const handleDeleteConfirm = async () => {
    try {
      const isBulk = selectedChecklists.length > 0;

      if (isBulk) {
        await deleteMultipleChecklists(selectedChecklists);
        setSnackbarMessage(
          `${selectedChecklists.length}件のチェックリストを削除しました。`
        );
      } else if (checklistToDelete) {
        await deleteChecklistLogically(checklistToDelete.public_id);

        const isTrash =
          folders.find((f) => f.id === selectedFolderId)?.name ===
          TRASH_FOLDER_NAME;
        const message = isTrash
          ? `「${checklistToDelete.title}」を完全に削除しました。`
          : `「${checklistToDelete.title}」をゴミ箱に移動しました。`;
        setSnackbarMessage(message);
      }

      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // reload list
      await fetchAndSetChecklists(pagination.page);
    } catch (error) {
      console.error("Failed to bulk delete checklists:", error);
      setSnackbarMessage(`削除に失敗しました: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDeleteMode(false);
      setSelectedChecklists([]);
      setChecklistToDelete(null);
      setConfirmDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  const currentFolderName =
    selectedFolderId === null
      ? "未分類"
      : folders.find((f) => f.public_id === selectedFolderId)?.name || "フォルダ";

  return (
    <Box>
      <PageHeader
        justify="flex-start"
        action={
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              onClick={handleCreateChecklistFromTemplate}
            >
              チェックリストを作成
            </Button>
            {isDeleteMode ? (
              <>
                <Button variant="outlined" onClick={toggleDeleteMode}>
                  キャンセル
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={selectedChecklists.length === 0}
                  onClick={handleBulkDeleteClick}
                >
                  選択項目を削除
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="error"
                onClick={toggleDeleteMode}
              >
                チェックリストを削除
              </Button>
            )}
          </Box>
        }
      />

      <Paper elevation={1} sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          フォルダ
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={0.75}>
          <Button
            variant={selectedFolderId === null ? "contained" : "outlined"}
            onClick={() => handleFolderSelect(null)}
            startIcon={<FolderIcon />}
          >
            未分類
          </Button>
          {folders
            .sort((a, b) => {
              if (a.name === TRASH_FOLDER_NAME) return 1;
              if (b.name === TRASH_FOLDER_NAME) return -1;
              return a.name.localeCompare(b.name);
            })
            .map((folder) => (
              <Button
                key={folder.id}
                variant={
                  selectedFolderId === folder.public_id ? "contained" : "outlined"
                }
                onClick={() => handleFolderSelect(folder.public_id)}
                startIcon={
                  folder.name === TRASH_FOLDER_NAME ? (
                    <DeleteIcon color="error" />
                  ) : (
                    <FolderIcon />
                  )
                }
                endIcon={
                  folder.name !== TRASH_FOLDER_NAME ? (
                    <Box
                      component="span"
                      sx={{ display: "flex", cursor: "pointer", ml: 0.5 }}
                      onClick={(e) => handleFolderMenuOpen(e, folder)}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <MoreVertIcon fontSize="small" />
                    </Box>
                  ) : null
                }
              >
                {folder.name}
              </Button>
            ))}
          <IconButton onClick={() => setAddFolderDialogOpen(true)} size="small">
            <AddIcon />
          </IconButton>
        </Box>
      </Paper>

      <Paper elevation={1} sx={{ p: 1.5, mb: 1.5 }}>
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          flexDirection={{ xs: "column", md: "row" }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="検索..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: { xs: "100%", md: 120 } }}>
            <Select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              displayEmpty
              size="small"
            >
              <MenuItem value="all">すべて</MenuItem>
              <MenuItem value="title">タイトル</MenuItem>
              <MenuItem value="assignee">担当者</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper elevation={2}>
        <List>
          {checklists.map((checklist) => (
            <ListItem
              key={checklist.id}
              divider
              style={{
                display: "block",
                paddingRight: "96px",
              }}
              sx={{
                cursor:
                  checklist.isEditing || isDeleteMode ? "default" : "pointer",
                "&:hover": {
                  backgroundColor:
                    checklist.isEditing || isDeleteMode
                      ? "inherit"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
              onClick={() => {
                if (isDeleteMode) {
                  handleSelectChecklistForDelete(checklist.public_id);
                } else if (!checklist.isEditing) {
                  handleChecklistClick(checklist.public_id);
                }
              }}
            >
              {isDeleteMode && (
                <Checkbox
                  edge="start"
                  checked={selectedChecklists.includes(checklist.public_id)}
                  tabIndex={-1}
                  disableRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectChecklistForDelete(checklist.public_id);
                  }}
                />
              )}
              {checklist.isEditing ? (
                <Box sx={{ py: 1 }}>
                  <TextField
                    fullWidth
                    label="タイトル"
                    value={editData.title}
                    onChange={(e) =>
                      setEditData({ ...editData, title: e.target.value })
                    }
                    margin="dense"
                  />
                  <TextField
                    fullWidth
                    label="担当者"
                    value={editData.assignee}
                    onChange={(e) =>
                      setEditData({ ...editData, assignee: e.target.value })
                    }
                    margin="dense"
                  />
                  <FormControl fullWidth margin="dense" size="small">
                    <InputLabel id={`folder-select-label-${checklist.id}`}>
                      フォルダ
                    </InputLabel>
                    <Select
                      labelId={`folder-select-label-${checklist.id}`}
                      value={editData.folder_id || "unclassified"}
                      label="フォルダ"
                      onChange={(e) =>
                        setEditData({ ...editData, folder_id: e.target.value })
                      }
                    >
                      <MenuItem value="unclassified">未分類</MenuItem>
                      {folders
                        .filter((f) => f.name !== TRASH_FOLDER_NAME)
                        .map((folder) => (
                          <MenuItem key={folder.id} value={folder.id}>
                            {folder.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ mt: 1 }}>
                    <SaveIconButton
                      onClick={() => handleEditSave(checklist)}
                    />
                    <CancelIconButton onClick={handleEditCancel} />
                  </Box>
                </Box>
              ) : (
                <>
                  <ListItemText
                    primary={checklist.title}
                    secondary={`担当者: ${checklist.assignee}`}
                  />
                  <ListItemSecondaryAction>
                    <EditIconButton
                      edge="end"
                      aria-label="edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditStart(checklist);
                      }}
                    />
                    <DeleteIconButton
                      edge="end"
                      aria-label="delete"
                      onClick={(e) => handleDeleteClick(e, checklist)}
                    />
                  </ListItemSecondaryAction>
                </>
              )}
            </ListItem>
          ))}
          {checklists.length === 0 && !loading && (
            <ListItem>
              <ListItemText
                primary={`${currentFolderName}にはチェックリストがありません。`}
              />
            </ListItem>
          )}
        </List>
      </Paper>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={pagination.pages}
          page={pagination.page}
          onChange={handlePageChange}
          color="primary"
          disabled={loading}
        />
      </Box>

      {/* Bulk delete confirm dialog */}
      <Dialog open={confirmDeleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(() => {
              const trashSelected =
                selectedFolderId &&
                folders.find((f) => f.public_id === selectedFolderId)?.name ===
                  TRASH_FOLDER_NAME;
              const isBulk = selectedChecklists.length > 0;
              if (isBulk) {
                return trashSelected
                  ? `選択した${selectedChecklists.length}件のチェックリストを完全に削除しますか？この操作は元に戻せません。`
                  : `選択した${selectedChecklists.length}件のチェックリストをゴミ箱に移動しますか？`;
              }
              if (!checklistToDelete) return "";
              return trashSelected
                ? `チェックリスト「${checklistToDelete.title}」を完全に削除しますか？この操作は元に戻せません。`
                : `チェックリスト「${checklistToDelete.title}」をゴミ箱に移動しますか？`;
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>キャンセル</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {(() => {
              const trashSelected =
                selectedFolderId &&
                folders.find((f) => f.public_id === selectedFolderId)?.name ===
                  TRASH_FOLDER_NAME;
              return trashSelected ? "完全に削除" : "ゴミ箱へ移動";
            })()}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addFolderDialogOpen}
        onClose={() => setAddFolderDialogOpen(false)}
      >
        <DialogTitle>新しいフォルダを作成</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="フォルダ名"
            type="text"
            fullWidth
            variant="standard"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAddFolder()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFolderDialogOpen(false)}>
            キャンセル
          </Button>
          <Button onClick={handleAddFolder}>作成</Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={folderMenuAnchorEl}
        open={Boolean(folderMenuAnchorEl)}
        onClose={handleFolderMenuClose}
      >
        <MenuItem
          onClick={() => {
            setEditingFolder(menuFolder);
            handleFolderMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          名前を変更
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeletingFolder(menuFolder);
            handleFolderMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          削除
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(editingFolder)}
        onClose={() => setEditingFolder(null)}
      >
        <DialogTitle>フォルダ名を変更</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="新しいフォルダ名"
            type="text"
            fullWidth
            variant="standard"
            value={editingFolder?.name || ""}
            onChange={(e) =>
              setEditingFolder({ ...editingFolder, name: e.target.value })
            }
            onKeyPress={(e) => e.key === "Enter" && handleUpdateFolder()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingFolder(null)}>キャンセル</Button>
          <Button onClick={handleUpdateFolder}>保存</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingFolder)}
        onClose={() => setDeletingFolder(null)}
      >
        <DialogTitle>フォルダを削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            フォルダ「{deletingFolder?.name}」を削除しますか？
            <br />
            このフォルダ内のすべてのチェックリストが完全に削除されます。この操作は元に戻せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingFolder(null)}>キャンセル</Button>
          <Button onClick={handleDeleteFolder} color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SelectCheckList;
